import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export const Route = createFileRoute("/_authenticated/pwa-diagnostics")({
  ssr: false,
  component: PwaDiagnosticsPage,
});

type SwInfo = {
  supported: boolean;
  controller: string | null;
  registrations: Array<{
    scope: string;
    scriptURL: string;
    state: "installing" | "waiting" | "active" | "redundant" | "unknown";
    updateViaCache: string;
  }>;
};

type CacheInfo = {
  name: string;
  entries: Array<{ url: string; status?: number; date?: string }>;
};

const LAST_UPDATE_KEY = "pwa:lastSwUpdate";

function fmtDate(iso: string | null): string {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

async function readSwInfo(): Promise<SwInfo> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return { supported: false, controller: null, registrations: [] };
  }
  const regs = await navigator.serviceWorker.getRegistrations();
  return {
    supported: true,
    controller: navigator.serviceWorker.controller?.scriptURL ?? null,
    registrations: regs.map((r) => {
      const w = r.active ?? r.waiting ?? r.installing;
      const state = r.active
        ? "active"
        : r.waiting
          ? "waiting"
          : r.installing
            ? "installing"
            : "unknown";
      return {
        scope: r.scope,
        scriptURL: w?.scriptURL ?? "(unknown)",
        state,
        updateViaCache: r.updateViaCache,
      };
    }),
  };
}

async function readCaches(): Promise<CacheInfo[]> {
  if (typeof caches === "undefined") return [];
  const names = await caches.keys();
  const all = await Promise.all(
    names.map(async (name) => {
      const cache = await caches.open(name);
      const reqs = await cache.keys();
      const entries = await Promise.all(
        reqs.slice(0, 200).map(async (req) => {
          const res = await cache.match(req);
          return {
            url: req.url,
            status: res?.status,
            date: res?.headers.get("date") ?? undefined,
          };
        }),
      );
      return { name, entries };
    }),
  );
  return all;
}

function PwaDiagnosticsPage() {
  const [sw, setSw] = useState<SwInfo | null>(null);
  const [cacheList, setCacheList] = useState<CacheInfo[]>([]);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [info, cList] = await Promise.all([readSwInfo(), readCaches()]);
      setSw(info);
      setCacheList(cList);
      setLastUpdate(localStorage.getItem(LAST_UPDATE_KEY));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onChange = () => {
      localStorage.setItem(LAST_UPDATE_KEY, new Date().toISOString());
      void refresh();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    return () =>
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
  }, [refresh]);

  const checkForUpdate = async () => {
    setBusy("update");
    setMessage(null);
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      if (regs.length === 0) {
        setMessage("No service worker registered. PWA features only run on the published site.");
      } else {
        await Promise.all(regs.map((r) => r.update()));
        localStorage.setItem(LAST_UPDATE_KEY, new Date().toISOString());
        setMessage("Update check complete.");
      }
      await refresh();
    } catch (e) {
      setMessage(`Update failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const clearCaches = async () => {
    setBusy("clear");
    setMessage(null);
    try {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
      setMessage(`Cleared ${names.length} cache(s).`);
      await refresh();
    } catch (e) {
      setMessage(`Clear failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const unregister = async () => {
    setBusy("unregister");
    setMessage(null);
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      setMessage(`Unregistered ${regs.length} worker(s). Reload to re-register.`);
      await refresh();
    } catch (e) {
      setMessage(`Unregister failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const totalEntries = cacheList.reduce((n, c) => n + c.entries.length, 0);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">PWA Diagnostics</h1>
        <p className="text-sm text-muted-foreground">
          Service worker status, cached assets, and update history for the installed app shell.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Service Worker</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!sw?.supported && (
            <p className="text-muted-foreground">Service workers are not supported in this browser.</p>
          )}
          {sw?.supported && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Controller</span>
                <span className="font-mono text-xs">{sw.controller ?? "none"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Registrations</span>
                <Badge variant={sw.registrations.length ? "default" : "secondary"}>
                  {sw.registrations.length}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last successful update</span>
                <span className="font-mono text-xs">{fmtDate(lastUpdate)}</span>
              </div>
              {sw.registrations.length > 0 && (
                <div className="space-y-2 rounded-md border border-border p-3">
                  {sw.registrations.map((r) => (
                    <div key={r.scope} className="space-y-1 text-xs">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Scope</span>
                        <span className="font-mono break-all">{r.scope}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Script</span>
                        <span className="font-mono break-all">{r.scriptURL}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">State</span>
                        <Badge variant={r.state === "active" ? "default" : "secondary"}>
                          {r.state}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" onClick={checkForUpdate} disabled={busy !== null}>
                  {busy === "update" ? "Checking…" : "Check for update"}
                </Button>
                <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
                  Refresh
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={unregister}
                  disabled={busy !== null || sw.registrations.length === 0}
                >
                  Unregister
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Caches ({cacheList.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total cached entries</span>
            <Badge variant="secondary">{totalEntries}</Badge>
          </div>
          {cacheList.length === 0 ? (
            <p className="text-muted-foreground">No caches present.</p>
          ) : (
            <ScrollArea className="h-72 rounded-md border border-border">
              <div className="divide-y divide-border">
                {cacheList.map((c) => (
                  <div key={c.name} className="p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-medium">{c.name}</span>
                      <Badge variant="outline">{c.entries.length}</Badge>
                    </div>
                    <ul className="space-y-1">
                      {c.entries.map((e) => (
                        <li
                          key={e.url}
                          className="font-mono text-[11px] break-all text-muted-foreground"
                        >
                          <span className="text-foreground">{e.status ?? "—"}</span> {e.url}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={clearCaches}
              disabled={busy !== null || cacheList.length === 0}
            >
              {busy === "clear" ? "Clearing…" : "Clear all caches"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {message && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
          {message}
        </div>
      )}
    </div>
  );
}